import { useShowDialog } from "@/components/ConfirmationDialogProvider";
import {
  AddEditContactDialog,
  ContactFormValues,
} from "@/components/contacts/add-dialog";
import { autoId } from "@/data/ids";
import { useFirestore } from "@/data/studio-react/firebase/useFirestore";
import { useStudioStream } from "@/data/studio-react/useStudioStream";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  EnvelopeIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  TrashIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import {
  deleteDoc,
  doc,
  GeoPoint,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Contact, Customer } from "../../../types";

interface TabProps {
  customer: Customer | null;
  marketId: string;
}

// Component to display the MartEye icon if a contact has a martEyeId
const MartEyeIcon = () => (
  <div className="inline-flex items-center justify-center rounded-full bg-blue-100 p-1">
    <span className="text-xs font-medium text-blue-800">ME</span>
  </div>
);

// Contact card component
const ContactCard = ({
  contact,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
}) => {
  let { showDialog } = useShowDialog();

  let market = useStudioStream("session:market");
  let countryCode = market?.countryCode;

  return (
    <div className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between ">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900">
            {contact.displayName || "Unnamed Contact"}
            {contact.marteyeUiId && (
              <span className="ml-2">
                <MartEyeIcon />
              </span>
            )}
          </h3>
          {contact.email &&
            (contact.accountEmails || contact.marketingEmails) && (
              <div className="mt-1 flex gap-1">
                {contact.accountEmails && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    CC on Invoices
                  </span>
                )}
                {contact.marketingEmails && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Marketing Emails
                  </span>
                )}
              </div>
            )}
        </div>
        <div className="flex items-center gap-2">
          <Menu>
            <MenuButton>
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <MenuItems
              anchor="bottom"
              className="min-w-[140px] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            >
              <MenuItem>
                <button
                  onClick={() => onEdit(contact)}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() =>
                    showDialog(
                      "Delete Contact",
                      "This will delete the contact permantly Are you sure?",
                      "Delete",
                      () => {
                        onDelete(contact);
                      },
                      "Cancel",
                      () => {},
                      "danger"
                    )
                  }
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      </div>

      <div className="space-y-2 text-sm text-gray-600 flex-grow">
        {contact.email && (
          <div className="flex items-center">
            <EnvelopeIcon className="mr-2 h-4 w-4" />
            <span>{contact.email}</span>
          </div>
        )}
        {contact.phoneNumber && (
          <div className="flex items-center">
            <PhoneIcon className="mr-2 h-4 w-4" />
            <span>{contact.phoneNumber}</span>
          </div>
        )}
        {contact.landlineNumber && (
          <div className="flex items-center">
            <PhoneIcon className="mr-2 h-4 w-4" />
            <span>{contact.landlineNumber}</span>
          </div>
        )}
        {contact.address &&
          (contact.address.address1 ||
            contact.address.address2 ||
            contact.address.city ||
            contact.address.province ||
            contact.address.zip) && (
            <div className="flex items-center">
              <MapPinIcon className="mr-2 h-4 w-4" />
              <span>
                {[
                  contact.address.address1,
                  contact.address.address2,
                  contact.address.city,
                  contact.address.province,
                  contact.address.zip,
                  countryCode === contact.address.country
                    ? ""
                    : contact.address.country,
                ].join(" ")}
              </span>
            </div>
          )}
      </div>

      {contact.notes && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-600">{contact.notes}</p>
        </div>
      )}
    </div>
  );
};

export function ContactsTab(props: TabProps) {
  let { customer, marketId } = props;
  let [isDialogOpen, setIsDialogOpen] = useState(false);
  let firestore = useFirestore();

  let [dialogValues, setDialogValues] = useState<ContactFormValues | null>(
    null
  );
  let contacts = useStudioStream("contacts", marketId, customer?.id ?? "");

  const _handleDeleteContact = async (contact: Contact) => {
    try {
      const customerId = props.customer?.id;
      const contactRef = doc(
        firestore,
        `markets/${marketId}/customers/${customerId}/contacts/${contact.id}`
      );
      await deleteDoc(contactRef);
      toast.success("Contact deleted");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete contact");
    }
  };

  const _handleSaveContact = async (values: ContactFormValues) => {
    const customerId = props.customer?.id;
    try {
      const contactRef = doc(
        firestore,
        `markets/${marketId}/customers/${customerId}/contacts/${values.id}`
      );

      const payload: Omit<Contact, "createdAt" | "updatedAt"> = {
        id: values.id || autoId(),
        displayName: values.displayName || "",
        email: values.email || "",
        phoneNumber: values.phoneNumber || "",
        landlineNumber: values.landlineNumber || "",
        notes: values.notes || "",
        address: {
          id: autoId(),
          address1: values.address1 || "",
          address2: values.address2 || "",
          city: values.city || "",
          province: values.province || "",
          zip: values.zip || "",
          country: values.country || "",
        },
        marketId: marketId,
        marteyeUiId: values.marteyeUiId || "",
        accountEmails: values.accountEmails || false,
        marketingEmails: values.marketingEmails || false,
      };

      let coordinates = values.coordinates as {
        latitude: number;
        longitude: number;
      } | null;
      if (coordinates && payload.address) {
        let addressCoordinates = new GeoPoint(
          coordinates.latitude,
          coordinates.longitude
        );
        payload.address.coordinates = addressCoordinates;
      }

      if (dialogValues) {
        // Update Contact
        await updateDoc(contactRef, {
          ...payload,
          updatedAt: Timestamp.now(),
        });
        toast.success("Contact updated");
      } else {
        // Create Contact
        await setDoc(contactRef, {
          ...payload,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        toast.success("Contact created");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save contact");
    } finally {
      handleDialogClose(false);
    }
  };

  useEffect(() => {
    if (dialogValues) {
      setIsDialogOpen(true);
    }
  }, [dialogValues]);

  function handleDialogClose(setOpen: boolean) {
    setIsDialogOpen(setOpen);
    setDialogValues(null);
  }
  return (
    <>
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Contacts</h2>
          <AddEditContactDialog
            isOpen={isDialogOpen}
            defaultValues={dialogValues}
            onSubmit={_handleSaveContact}
            setIsOpen={handleDialogClose}
          />
        </div>

        {contacts.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-6 text-center">
            <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No contacts
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a new contact.
            </p>
            <div className="mt-6">
              <AddEditContactDialog
                isOpen={isDialogOpen}
                setIsOpen={handleDialogClose}
                defaultValues={dialogValues}
                onSubmit={_handleSaveContact}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {contacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onDelete={_handleDeleteContact}
                onEdit={() => {
                  setDialogValues({
                    notes: contact.notes ?? "",
                    id: contact.id,
                    displayName: contact.displayName,
                    email: contact.email,
                    phoneNumber: contact.phoneNumber ?? "",
                    landlineNumber: contact.landlineNumber ?? "",
                    address1: contact.address?.address1 ?? "",
                    address2: contact.address?.address2 ?? "",
                    city: contact.address?.city ?? "",
                    province: contact.address?.province ?? "",
                    zip: contact.address?.zip ?? "",
                    country: contact.address?.country ?? "",
                    coordinates: contact.address?.coordinates ?? null,
                    accountEmails: contact.accountEmails ?? false,
                    marketingEmails: contact.marketingEmails ?? false,
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
